import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchAuctionTeamSetup,
  fetchAuctionTeamPlayers,
  fetchAuctionTournamentPlayers,
  fetchLiveAuctionTeamSetup,
  fetchLiveAuctionTeamPlayers,
  fetchLiveAuctionTournamentPlayers,
  fetchTournamentById,
  fetchTournamentRegisteredPlayers,
} from '../../../services/tournamentService';
import {
  fetchLocalStreamPreview,
  fetchMatchById,
  fetchMatchConfiguration,
  fetchMatchStreamDetails,
  fetchMatchLiveStatus,
  fetchRelayStatus,
  generateMatchYouTubeStream,
  startRelay,
  stopRelay,
  updateMatchConfiguration,
} from '../../../services/matchService';
import { fetchOrganizationOfficials } from '../../../services/organizationService';
import { fetchClubOfficials } from '../../../services/clubService';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';
import { buildPlayerDirectory, normalizeId, resolveRosterByIds, uniqueById } from '../utils/playerDirectory';



const BALL_MATERIAL_OPTIONS = [
  { value: 'tennis', label: 'Tennis' },
  { value: 'leather', label: 'Leather' },
  { value: 'synthetic', label: 'Synthetic' },
  { value: 'plastic', label: 'Plastic' },
  { value: 'other', label: 'Other' },
];

const BALL_COLOR_OPTIONS = [
  { value: 'green', label: 'Green' },
  { value: 'red', label: 'Red' },
  { value: 'white', label: 'White' },
  { value: 'pink', label: 'Pink' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'orange', label: 'Orange' },
  { value: 'other', label: 'Other' },
];

const STREAMING_MODES = {
  DIRECT_MOBILE: 'DIRECT_MOBILE',
  OBS: 'OBS',
  LOCAL_MAC_TESTING: 'LOCAL_MAC_TESTING',
};

const LEGACY_STREAMING_MODE_MAP = {
  DIRECT_MOBILE_TO_YOUTUBE: STREAMING_MODES.DIRECT_MOBILE,
  OBS_TO_YOUTUBE: STREAMING_MODES.OBS,
  LOCAL_MAC_TEST: STREAMING_MODES.LOCAL_MAC_TESTING,
};

const STREAM_PROVIDERS = {
  YOUTUBE: 'YOUTUBE',
  FACEBOOK: 'FACEBOOK',
};

const LEGACY_STREAM_PROVIDER_MAP = {
  youtube: STREAM_PROVIDERS.YOUTUBE,
  facebook: STREAM_PROVIDERS.FACEBOOK,
};

const DEFAULT_OBS_CAMERA_COUNT = 3;
const OVER_END_AD_MODES = ['off', 'custom_only', 'default_only', 'custom_then_default'];
const OVER_END_AD_FREQUENCIES = ['every_over', 'every_2_overs', 'every_3_overs'];

const normalizeTournamentRoleKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[\s-]+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '');

const getTournamentRoleKeys = (player = {}) => {
  const sources = [player?.tournamentRoles, player?.registeredPlayer?.tournamentRoles, player?.registration?.tournamentRoles, player?.player?.tournamentRoles, player?.originalPlayer?.tournamentRoles];
  const roles = [];
  let hasTournamentRoles = false;
  sources.forEach((source) => {
    if (Array.isArray(source)) {
      hasTournamentRoles = true;
      source.forEach((role) => {
        const normalized = normalizeTournamentRoleKey(role);
        if (normalized) roles.push(normalized);
      });
    }
  });
  return hasTournamentRoles ? Array.from(new Set(roles)) : ['player'];
};

const hasTournamentRole = (player, role) => getTournamentRoleKeys(player).includes(normalizeTournamentRoleKey(role));

const DEFAULT_OVER_END_ADS = { enabled: true, mode: 'custom_then_default', durationSeconds: 8, frequency: 'every_over', startAfterOver: 1, showScoreBugDuringAd: false, allowDefaultAds: true };


const LIVE_HEALTH_BADGE = {
  live: { label: 'Live', className: 'bg-success-subtle text-success' },
  connecting: { label: 'Connecting', className: 'bg-warning-subtle text-warning-emphasis' },
  offline: { label: 'Offline', className: 'bg-danger-subtle text-danger' },
  stream_lost: { label: 'Stream lost', className: 'bg-danger-subtle text-danger' },
};

const resolveLiveHealth = (liveStatus = {}) => {
  const streamHealth = String(liveStatus?.streamHealth || '').trim().toLowerCase();
  if (streamHealth && LIVE_HEALTH_BADGE[streamHealth]) return streamHealth;

  const streamStatus = String(liveStatus?.streamStatus || '').trim().toLowerCase();
  if (streamStatus === 'active') return 'live';
  if (liveStatus?.isLive) return 'stream_lost';
  return 'offline';
};

const normalizeCameraCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 1) return DEFAULT_OBS_CAMERA_COUNT;
  return Math.min(6, Math.floor(numeric));
};

const buildObsCameras = ({ matchIdentifier = '', ingestUrl = '', cameraCount = DEFAULT_OBS_CAMERA_COUNT, cameras = [] } = {}) => {
  const normalizedCount = normalizeCameraCount(cameraCount);
  const keySeed = String(matchIdentifier || 'match')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'match';
  const normalizedIngest = String(ingestUrl || '').trim().replace(/\/$/, '');

  return Array.from({ length: normalizedCount }, (_, index) => {
    const existingCamera = cameras[index] || {};
    const streamKey = String(existingCamera.streamKey || `${keySeed}_cam${index + 1}`).trim() || `${keySeed}_cam${index + 1}`;
    const cameraIngest = String(existingCamera.ingestUrl || normalizedIngest || '').trim().replace(/\/$/, '');
    const publishUrl = String(existingCamera.publishUrl || (cameraIngest && streamKey ? `${cameraIngest}/${streamKey}` : '')).trim();

    return {
      id: String(existingCamera.id || `cam${index + 1}`).trim() || `cam${index + 1}`,
      label: String(existingCamera.label || `Camera ${index + 1}`).trim() || `Camera ${index + 1}`,
      ingestUrl: cameraIngest,
      streamKey,
      publishUrl,
    };
  });
};


const buildObsConfig = ({ sourceObsConfig = {}, fallbackIngestUrl = '', fallbackMatchIdentifier = '' } = {}) => {
  const ingestUrl = String(sourceObsConfig.ingestUrl || fallbackIngestUrl || '').trim();
  const cameraCount = normalizeCameraCount(sourceObsConfig.cameraCount);
  const cameras = buildObsCameras({
    matchIdentifier: fallbackMatchIdentifier,
    ingestUrl,
    cameraCount,
    cameras: Array.isArray(sourceObsConfig.cameras) ? sourceObsConfig.cameras : [],
  });

  return {
    cameraCount,
    ingestUrl,
    cameras,
  };
};

const normalizeStreamingMode = (mode) => {
  if (!mode || typeof mode !== 'string') return STREAMING_MODES.DIRECT_MOBILE;
  return LEGACY_STREAMING_MODE_MAP[mode] || mode;
};

const normalizeStreamProvider = (provider) => {
  if (!provider || typeof provider !== 'string') return STREAM_PROVIDERS.YOUTUBE;
  return LEGACY_STREAM_PROVIDER_MAP[provider.toLowerCase()] || provider.toUpperCase();
};

const toLegacyStreamingMode = (mode) => {
  if (mode === STREAMING_MODES.OBS) return 'OBS_TO_YOUTUBE';
  if (mode === STREAMING_MODES.LOCAL_MAC_TESTING) return 'LOCAL_MAC_TEST';
  return 'DIRECT_MOBILE_TO_YOUTUBE';
};

const isDevLikeEnvironment = () => {
  const nodeEnv = typeof process !== 'undefined' ? process.env?.NODE_ENV : undefined;
  if (nodeEnv && nodeEnv !== 'production') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = (window.location?.hostname || '').toLowerCase();
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
};

const normalizeOverEndAds = (settings = {}) => { const source = settings || {}; const mode = OVER_END_AD_MODES.includes(source.mode) ? source.mode : (source.enabled === false ? 'off' : DEFAULT_OVER_END_ADS.mode); return { ...DEFAULT_OVER_END_ADS, ...source, enabled: source.enabled !== false && mode !== 'off', mode, durationSeconds: Math.max(1, Number(source.durationSeconds || DEFAULT_OVER_END_ADS.durationSeconds)), frequency: OVER_END_AD_FREQUENCIES.includes(source.frequency) ? source.frequency : DEFAULT_OVER_END_ADS.frequency, startAfterOver: Math.max(1, Number(source.startAfterOver || DEFAULT_OVER_END_ADS.startAfterOver)), showScoreBugDuringAd: Boolean(source.showScoreBugDuringAd), allowDefaultAds: source.allowDefaultAds !== false }; };

const buildStreamingConfig = (configuration = {}, { fallbackMatchIdentifier = '' } = {}) => ({
  mode: normalizeStreamingMode(configuration?.streamingConfig?.mode || configuration?.streamingMode),
  overlayMode: 'burn_in',
  youtube: {
    ingestUrl: configuration?.streamingConfig?.youtube?.ingestUrl || configuration?.youtubeIngestUrl || 'rtmp://a.rtmp.youtube.com/live2',
    streamKey: configuration?.streamingConfig?.youtube?.streamKey || configuration?.youtubeStreamKey || configuration?.rtmpStreamKey || '',
    playbackUrl: configuration?.streamingConfig?.youtube?.playbackUrl || configuration?.youtubeLiveLink || configuration?.livePlaybackUrl || configuration?.streamUrl || '',
    videoId: configuration?.streamingConfig?.youtube?.videoId || configuration?.youtubeVideoId || '',
  },
  local: {
    ingestUrl: configuration?.streamingConfig?.local?.ingestUrl || configuration?.rtmpIngestUrl || '',
    streamKey: configuration?.streamingConfig?.local?.streamKey || configuration?.rtmpStreamKey || '',
  },
  obs: {
    ingestUrl: configuration?.streamingConfig?.obs?.ingestUrl || configuration?.rtmpIngestUrl || '',
    streamKey: configuration?.streamingConfig?.obs?.streamKey || configuration?.rtmpStreamKey || '',
    playbackUrl: configuration?.streamingConfig?.obs?.playbackUrl || configuration?.livePlaybackUrl || configuration?.streamUrl || '',
  },
  obsConfig: buildObsConfig({
    sourceObsConfig: configuration?.streamingConfig?.obsConfig || configuration?.obsConfig || {},
    fallbackIngestUrl: configuration?.streamingConfig?.obs?.ingestUrl || configuration?.rtmpIngestUrl || '',
    fallbackMatchIdentifier: configuration?.matchId || configuration?._id || configuration?.id || fallbackMatchIdentifier || '',
  }),
  overEndAds: normalizeOverEndAds(configuration?.streamingConfig?.overEndAds || configuration?.overEndAds),
});

const buildInitialConfig = (source) => {
  const configuration = source?.configuration || source || {};
  const resolvedPlayingCount = Number(configuration?.playingCount ?? configuration?.playersPerSide) || 0;
  const getMatchPlayerEntries = (teamKey) => {
    const matchPlayers = configuration?.[`${teamKey}MatchPlayers`];
    return Array.isArray(matchPlayers) ? matchPlayers : [];
  };
  const getSquadPlayers = (teamKey) => {
    const squadPlayers = configuration?.[`${teamKey}SquadPlayers`];
    if (Array.isArray(squadPlayers)) return squadPlayers;
    const legacyPlayers = configuration?.[`${teamKey}Players`];
    return Array.isArray(legacyPlayers) ? legacyPlayers : [];
  };
  const getActivePlayers = (teamKey) => {
    const explicitPlayingPlayers = configuration?.[`${teamKey}PlayingPlayers`];
    if (Array.isArray(explicitPlayingPlayers)) return explicitPlayingPlayers;
    const playingFromMatchPlayers = getMatchPlayerEntries(teamKey)
      .filter((entry) => entry?.status === 'playing')
      .map((entry) => entry?.playerId)
      .filter(Boolean);
    if (playingFromMatchPlayers.length) return playingFromMatchPlayers;
    return [];
  };
  const getSubstitutePlayers = (teamKey) => {
    const explicitSubstitutes = configuration?.[`${teamKey}SubstitutePlayers`];
    if (Array.isArray(explicitSubstitutes)) return explicitSubstitutes;
    return getMatchPlayerEntries(teamKey)
      .filter((entry) => entry?.status === 'substitute')
      .map((entry) => entry?.playerId)
      .filter(Boolean);
  };
  const normalizedProvider = normalizeStreamProvider(
    configuration?.streamProvider || configuration?.streamingConfig?.provider,
  );
  return ({
    venue: configuration?.venue || '',
    notes: configuration?.notes || '',
    scorer: configuration?.scorer || '',
    umpire1: configuration?.umpire1 || '',
    umpire2: configuration?.umpire2 || '',
    matchReferee: configuration?.matchReferee || '',
    streamUrl: configuration?.streamUrl || '',
    youtubeLiveLink: configuration?.youtubeLiveLink || configuration?.streamUrl || configuration?.livePlaybackUrl || '',
    streamProvider: normalizedProvider,
    streamingConfig: buildStreamingConfig(configuration, {
      fallbackMatchIdentifier: source?.matchId || source?._id || source?.id || '',
    }),
    overlayWebUrl: configuration?.overlayWebUrl || '',
    overlayAuthToken: configuration?.overlayAuthToken || '',
    tossWinner: configuration?.tossWinner || '',
    tossDecision: configuration?.tossDecision || '',
    ball: {
      material: configuration?.ball?.material || 'tennis',
      color: configuration?.ball?.color || 'yellow',
    },
    overs: configuration?.overs || '',
    maxOversPerPlayer: configuration?.maxOversPerPlayer || '',
    maxPlayersAtMaxOvers: configuration?.maxPlayersAtMaxOvers || '',
    playersPerSide: configuration?.playersPerSide || '',
    squadSize: configuration?.squadSize || configuration?.playersPerSide || '',
    allowLastBatsman: Boolean(configuration?.allowLastBatsman),
    team1SquadPlayers: getSquadPlayers('team1'),
    team2SquadPlayers: getSquadPlayers('team2'),
    team1PlayingPlayers: getActivePlayers('team1'),
    team2PlayingPlayers: getActivePlayers('team2'),
    team1SubstitutePlayers: getSubstitutePlayers('team1'),
    team2SubstitutePlayers: getSubstitutePlayers('team2'),
    team1Players: getSquadPlayers('team1'),
    team2Players: getSquadPlayers('team2'),
    team1Captain: configuration?.team1Captain || '',
    team1ViceCaptain: configuration?.team1ViceCaptain || '',
    team1WicketKeeper: configuration?.team1WicketKeeper || '',
    team2Captain: configuration?.team2Captain || '',
    team2ViceCaptain: configuration?.team2ViceCaptain || '',
    team2WicketKeeper: configuration?.team2WicketKeeper || '',
  });
};

const resolveYouTubePublicStreamUrl = (youtubeStreamKey = '', rtmpStreamKey = '') => {
  const preferredValue =
    typeof youtubeStreamKey === 'string' && youtubeStreamKey.trim()
      ? youtubeStreamKey
      : rtmpStreamKey;
  const rawValue = typeof preferredValue === 'string' ? preferredValue.trim() : '';
  if (!rawValue) {
    return '';
  }

  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue;
  }

  const sanitizedKey = rawValue
    .replace(/^.*live\//i, '')
    .replace(/^.*watch\?v=/i, '')
    .replace(/^.*youtu\.be\//i, '')
    .replace(/[?&].*$/, '')
    .trim();

  if (!sanitizedKey) {
    return '';
  }

  return `https://www.youtube.com/live/${sanitizedKey}`;
};

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>

const resolveYouTubeLiveLinkFromResponse = (payload = {}) => {
  const videoId = [payload?.videoId, payload?.youtubeVideoId, payload?.streamingConfig?.youtube?.videoId]
    .find((value) => typeof value === 'string' && value.trim());
  if (videoId) {
    return `https://www.youtube.com/watch?v=${videoId.trim()}`;
  }

  const candidates = [
    payload?.youtubePlaybackUrl,
    payload?.livePlaybackUrl,
    payload?.playbackUrl,
    payload?.streamUrl,
    payload?.youtubeLiveLink,
    payload?.youtubeUrl,
    payload?.liveUrl,
    payload?.streamingConfig?.youtube?.playbackUrl,
    payload?.streamingConfig?.youtube?.youtubeUrl,
    payload?.configuration?.youtubePlaybackUrl,
    payload?.configuration?.livePlaybackUrl,
    payload?.configuration?.streamUrl,
    payload?.configuration?.youtubeLiveLink,
  ];

  const directUrl = candidates.find((value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim()));
  if (directUrl) return directUrl.trim();

  const channelId = [payload?.channelId, payload?.youtubeChannelId]
    .find((value) => typeof value === 'string' && value.trim());
  if (channelId) {
    return `https://www.youtube.com/channel/${channelId.trim()}/live`;
  }

  return '';
};

const MatchConfigurationPage = () => {
  const {
    organizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    individualTournamentId,
    tournamentId,
    managerId,
    matchId,
  } = useParams();
  const { token } = useAuth();
  const communityId = useMemo(() => getCommunityId(routeCommunityId), [routeCommunityId]);
  const clubId = useMemo(() => getClubId(routeClubId), [routeClubId]);
  const [tournament, setTournament] = useState(null);
  const [match, setMatch] = useState(null);
  const [configForm, setConfigForm] = useState(buildInitialConfig(null));
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [teamPlayers, setTeamPlayers] = useState({ team1: [], team2: [] });
  const [playersError, setPlayersError] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [streamUrlStatus, setStreamUrlStatus] = useState('');
  const [localPreviewEnabled, setLocalPreviewEnabled] = useState(false);
  const [localPreviewStatus, setLocalPreviewStatus] = useState('');
  const [localPreviewConfig, setLocalPreviewConfig] = useState(null);
  const [streamDetails, setStreamDetails] = useState(null);
  const [relayStatus, setRelayStatus] = useState(null);
  const [officials, setOfficials] = useState([]);
  const [officialsError, setOfficialsError] = useState('');
  const [isGeneratingYouTube, setIsGeneratingYouTube] = useState(false);
  const [liveStatus, setLiveStatus] = useState(null);
  const [liveStatusError, setLiveStatusError] = useState('');

  const publicViewLink = useMemo(() => {
    if (!match?.publicViewLink) {
      return '';
    }

    if (match.publicViewLink.startsWith('http')) {
      return match.publicViewLink;
    }

    if (typeof window === 'undefined' || !window.location?.origin) {
      return match.publicViewLink;
    }

    const normalizedPath = match.publicViewLink.startsWith('/')
      ? match.publicViewLink
      : `/${match.publicViewLink}`;
    return `${window.location.origin}${normalizedPath}`;
  }, [match?.publicViewLink]);

  const isLocalDev = useMemo(() => isDevLikeEnvironment(), []);
  const streamingMode = normalizeStreamingMode(configForm.streamingConfig?.mode);
  const isObsMode = streamingMode === STREAMING_MODES.OBS;
  const isLocalMode = streamingMode === STREAMING_MODES.LOCAL_MAC_TESTING;
  const isDirectMode = streamingMode === STREAMING_MODES.DIRECT_MOBILE;
  const selectedStreamProvider = normalizeStreamProvider(configForm.streamProvider);
  const isYouTubeProvider = selectedStreamProvider === STREAM_PROVIDERS.YOUTUBE;
  const liveStatusMode = liveStatus?.streamingMode || streamDetails?.streamingMode || '—';
  const liveStatusProvider = liveStatus?.provider || streamDetails?.streamProvider || '—';
  const livePlaybackUrl = liveStatus?.playbackUrl || streamDetails?.youtube?.playbackUrl || configForm.streamingConfig?.youtube?.playbackUrl || '';
  const obsYouTubeIngestUrl = streamDetails?.youtube?.ingestUrl || configForm.streamingConfig?.youtube?.ingestUrl || '';
  const obsYouTubeStreamKey = streamDetails?.youtube?.streamKey || liveStatus?.streamKey || configForm.streamingConfig?.youtube?.streamKey || '';
  const liveHealthKey = resolveLiveHealth(liveStatus);
  const liveHealthBadge = LIVE_HEALTH_BADGE[liveHealthKey] || LIVE_HEALTH_BADGE.offline;
  const liveStreamStatusLabel = liveStatus?.streamStatus || 'unknown';

  useEffect(() => {
    if (isLocalDev || !isLocalMode) {
      return;
    }

    setConfigForm((prev) => ({
      ...prev,
      streamingConfig: {
        ...prev.streamingConfig,
        mode: STREAMING_MODES.DIRECT_MOBILE,
      },
    }));
  }, [isLocalDev, isLocalMode]);


  useEffect(() => {
    const loadMatch = async () => {
      if (!token || !tournamentId) {
        setTournament(null);
        setMatch(null);
        setErrorMessage('Login required to view match details.');
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      const [tournamentRes, matchRes, configRes] = await Promise.allSettled([
        fetchTournamentById(tournamentId, token, organizationId, { communityId, clubId }),
        fetchMatchById({ token, tournamentId, matchId }),
        fetchMatchConfiguration({ token, tournamentId, matchId }),
      ]);

      const loadedTournament =
        tournamentRes.status === 'fulfilled' ? (tournamentRes.value?.tournament || null) : null;
      setTournament(loadedTournament);

      if (matchRes.status !== 'fulfilled' || !matchRes.value?.match) {
        setMatch(null);
        setConfigForm(buildInitialConfig(null));
        setErrorMessage('Match not found.');
        setIsLoading(false);
        return;
      }

      const loadedMatch = {
        ...matchRes.value.match,
        id: String(matchRes.value.match.id || matchRes.value.match._id || matchId),
      };
      const configPayload =
        configRes.status === 'fulfilled'
          ? (configRes.value?.configuration || configRes.value?.streamDetails || configRes.value)
          : null;
      const parsedConfig = buildInitialConfig(configPayload || loadedMatch);
      const team1StatusById = Object.fromEntries(
        (configPayload?.team1MatchPlayers || loadedMatch?.configuration?.team1MatchPlayers || [])
          .map((item) => [item?.playerId, item?.status])
          .filter(([playerId]) => Boolean(playerId)),
      );
      const team2StatusById = Object.fromEntries(
        (configPayload?.team2MatchPlayers || loadedMatch?.configuration?.team2MatchPlayers || [])
          .map((item) => [item?.playerId, item?.status])
          .filter(([playerId]) => Boolean(playerId)),
      );
      const team1SquadRows = (parsedConfig.team1SquadPlayers || []).map((playerId) => ({
        id: playerId,
        status: team1StatusById[playerId] || 'unannounced',
      }));
      const team2SquadRows = (parsedConfig.team2SquadPlayers || []).map((playerId) => ({
        id: playerId,
        status: team2StatusById[playerId] || 'unannounced',
      }));

      setMatch(loadedMatch);
      setConfigForm(parsedConfig);
      setIsLoading(false);
    };

    loadMatch().catch((error) => {
      setErrorMessage(error.message || 'Unable to load match configuration.');
      setIsLoading(false);
    });
  }, [clubId, communityId, matchId, organizationId, token, tournamentId]);

  useEffect(() => {
    const loadStreamDetails = async () => {
      if (!token || !matchId || !tournamentId) return;
      try {
        const details = await fetchMatchStreamDetails({ token, matchId, tournamentId });
        setStreamDetails(details);
      } catch (_error) {
        setStreamDetails(null);
      }

      if (!isObsMode) {
        setRelayStatus(null);
        return;
      }

      try {
        const relay = await fetchRelayStatus({ token, matchId, tournamentId });
        setRelayStatus(relay?.relayStatus || null);
      } catch (_error) {
        setRelayStatus(null);
      }
    };
    loadStreamDetails();
  }, [token, matchId, tournamentId, configForm.streamingConfig?.mode, isObsMode]);


  useEffect(() => {
    let intervalId = null;

    const loadLiveStatus = async () => {
      if (!token || !matchId || !tournamentId) return;
      try {
        const statusPayload = await fetchMatchLiveStatus({ token, matchId, tournamentId });
        setLiveStatus(statusPayload || null);
        setLiveStatusError('');
      } catch (error) {
        setLiveStatus(null);
        setLiveStatusError(error?.message || 'Unable to load live status.');
      }
    };

    loadLiveStatus();
    if (liveStatus?.isLive) {
      intervalId = window.setInterval(loadLiveStatus, 5000);
    }

    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [token, matchId, tournamentId, liveStatus?.isLive]);

  useEffect(() => {
    const loadTeamPlayers = async () => {
      if (!token || !tournamentId || !match) {
        setTeamPlayers({ team1: [], team2: [] });
        return;
      }

      setPlayersError('');

      const hydratedDirectory = buildPlayerDirectory({
        directory: match?.playerDirectory || match?.configuration?.playerDirectory || {},
        teamData: [
          ...(match?.configuration?.team1PlayersData || []),
          ...(match?.configuration?.team2PlayersData || []),
          ...(match?.configuration?.team1SquadPlayersData || []),
          ...(match?.configuration?.team2SquadPlayersData || []),
        ],
      });
      const hydratedTeam1 = resolveRosterByIds({
        rosterIds:
          match?.configuration?.team1SquadPlayers?.length
            ? match.configuration.team1SquadPlayers
            : (match?.configuration?.team1Players || []),
        playerDirectory: hydratedDirectory,
        matchId: match?.id || match?._id || matchId,
      }).filter((entry) => hasTournamentRole(entry, 'Player')).map((entry) => ({ ...entry, role: '' }));
      const hydratedTeam2 = resolveRosterByIds({
        rosterIds:
          match?.configuration?.team2SquadPlayers?.length
            ? match.configuration.team2SquadPlayers
            : (match?.configuration?.team2Players || []),
        playerDirectory: hydratedDirectory,
        matchId: match?.id || match?._id || matchId,
      }).filter((entry) => hasTournamentRole(entry, 'Player')).map((entry) => ({ ...entry, role: '' }));

      if (hydratedTeam1.length || hydratedTeam2.length) {
        setTeamPlayers({
          team1: hydratedTeam1,
          team2: hydratedTeam2,
        });
        return;
      }

      const normalize = (value = '') => value.trim().toLowerCase();
      const team1Name = normalize(match.team1);
      const team2Name = normalize(match.team2);
      const isManualFormat = tournament?.matchSchedule?.meta?.format === 'manual';

      const buildRosterLookup = (roster = []) =>
        new Map(
          roster
            .map((player, index) => {
              const playerId = player?.id || player?._id;
              if (!playerId) return null;
              return [
                playerId,
                {
                  id: playerId,
                  name:
                    player?.name ||
                    player?.fullName ||
                    player?.personal?.fullName ||
                    player?.user?.name ||
                    `Player ${index + 1}`,
                  role: player?.role || player?.primaryRole || player?.skills?.primaryRole || '',
                  tournamentRoles: player?.tournamentRoles,
                },
              ];
            })
            .filter(Boolean),
        );

      const buildDisplayRoster = (roster = []) =>
        roster
          .filter((player) => hasTournamentRole(player, 'Player'))
          .map((player, index) => {
            const playerId = player?.id || player?._id;
            if (!playerId) return null;
            return {
              id: playerId,
              name:
                player?.name ||
                player?.fullName ||
                player?.personal?.fullName ||
                player?.user?.name ||
                `Player ${index + 1}`,
              role: player?.role || player?.primaryRole || player?.skills?.primaryRole || '',
              tournamentRoles: player?.tournamentRoles,
            };
          })
          .filter(Boolean);

      try {
        if (isManualFormat) {
          const fallbackRoster = [...(tournament?.players || []), ...(tournament?.invitedPlayers || [])];
          let rosterSource = fallbackRoster;

          try {
            const response = await fetchTournamentRegisteredPlayers(
              tournamentId,
              token,
              organizationId,
              { communityId, clubId }
            );
            if (Array.isArray(response?.players) && response.players.length > 0) {
              rosterSource = response.players;
            }
          } catch (error) {
            setPlayersError(error.message || 'Unable to load registered players.');
          }

          const displayRoster = buildDisplayRoster(rosterSource);

          setTeamPlayers({
            team1: displayRoster,
            team2: displayRoster,
          });
          return;
        }

        const isLiveAuction = tournament?.auctionMode === 'live';
        const [teamSetupResult, rosterResult] = await Promise.allSettled([
          isLiveAuction
            ? fetchLiveAuctionTeamSetup(tournamentId, token)
            : fetchAuctionTeamSetup(tournamentId, token),
          isLiveAuction
            ? fetchLiveAuctionTeamPlayers(tournamentId, token)
            : fetchAuctionTeamPlayers(tournamentId, token),
        ]);

        if (teamSetupResult.status === 'rejected') {
          throw teamSetupResult.reason;
        }

        const teams = teamSetupResult.value?.setup?.teams || [];
        const rawRoster =
          rosterResult.status === 'fulfilled'
            ? rosterResult.value?.players || rosterResult.value || []
            : [];
        let fallbackRoster = [];

        if (!rawRoster.length) {
          try {
            const rosterResponse = isLiveAuction
              ? await fetchLiveAuctionTournamentPlayers(tournamentId, token)
              : await fetchAuctionTournamentPlayers(tournamentId, token);
            fallbackRoster = rosterResponse?.players || rosterResponse || [];
          } catch (error) {
            setPlayersError(error.message || 'Unable to load registered players.');
          }
        }

        const normalizedRoster = [...rawRoster, ...fallbackRoster]
          .map((player) => player.player || player)
          .filter(Boolean);
        const playerLookup = buildRosterLookup(normalizedRoster);
        const toDisplayPlayer = (player, index) => {
          if (!player) return null;
          const playerId = player.id || player._id || (typeof player === 'string' ? player : '');
          const resolved =
            player.player ||
            (playerId && playerLookup.has(playerId) ? playerLookup.get(playerId) : null) ||
            player;
          const resolvedId = playerId || resolved?.id || resolved?._id || '';
          if (!resolvedId || !hasTournamentRole(resolved, 'Player')) return null;
          return {
            id: resolvedId,
            name:
              resolved?.name ||
              resolved?.fullName ||
              resolved?.personal?.fullName ||
              resolved?.user?.name ||
              `Player ${index + 1}`,
            role: resolved?.role || resolved?.primaryRole || resolved?.skills?.primaryRole || '',
            tournamentRoles: resolved?.tournamentRoles,
          };
        };
        const team1 = teams.find((team) => normalize(team.name) === team1Name);
        const team2 = teams.find((team) => normalize(team.name) === team2Name);

        const displayRoster = buildDisplayRoster(normalizedRoster);
        const team1Roster = (team1?.players || [])
          .map((player, index) => toDisplayPlayer(player, index))
          .filter(Boolean);
        const team2Roster = (team2?.players || [])
          .map((player, index) => toDisplayPlayer(player, index))
          .filter(Boolean);

        setTeamPlayers({
          team1: team1Roster.length ? team1Roster : displayRoster,
          team2: team2Roster.length ? team2Roster : displayRoster,
        });

        if (rosterResult.status === 'rejected') {
          setPlayersError(rosterResult.reason?.message || 'Unable to load team players.');
        }
      } catch (error) {
        setPlayersError(error.message || 'Unable to load team players.');
        setTeamPlayers({ team1: [], team2: [] });
      }
    };

    loadTeamPlayers();
  }, [
    clubId,
    communityId,
    match,
    organizationId,
    token,
    tournament?.auctionMode,
    tournament?.invitedPlayers,
    tournament?.matchSchedule?.meta?.format,
    tournament?.players,
    tournamentId,
  ]);

  useEffect(() => {
    const loadOfficials = async () => {
      if (!token || (!organizationId && !clubId)) {
        setOfficials([]);
        return;
      }

      setOfficialsError('');

      try {
        if (tournamentId) {
          const registeredResponse = await fetchTournamentRegisteredPlayers(
            tournamentId,
            token,
            organizationId,
            { communityId, clubId },
          );
          const registeredOfficials = Array.isArray(registeredResponse?.players) ? registeredResponse.players : [];
          if (registeredOfficials.length) {
            setOfficials(registeredOfficials);
            return;
          }
        }

        const response = clubId
          ? await fetchClubOfficials(clubId, token)
          : await fetchOrganizationOfficials(organizationId, token);
        setOfficials(response.officials || []);
      } catch (error) {
        setOfficialsError(error.message || 'Unable to load officials.');
        setOfficials([]);
      }
    };

    loadOfficials();
  }, [clubId, communityId, organizationId, token, tournamentId]);

  useEffect(() => {
    setCopyStatus('');
  }, [publicViewLink]);

  const matchLabel = useMemo(() => {
    if (!match) return '';
    const name = `Match ${match.matchNumber}`;
    const fixture = match.team1 && match.team2 ? `${match.team1} vs ${match.team2}` : '';
    return [name, fixture].filter(Boolean).join(' • ');
  }, [match]);

  const buildSelectedRoster = (selectedIds, roster) => {
    if (!selectedIds.length) return [];
    const rosterLookup = new Map(roster.map((player) => [normalizeId(player), player]));
    const hydratedDirectory = buildPlayerDirectory({
      directory: match?.playerDirectory || match?.configuration?.playerDirectory || {},
      teamData: [
        ...(match?.configuration?.team1PlayersData || []),
        ...(match?.configuration?.team2PlayersData || []),
      ],
    });
    return uniqueById(selectedIds).map((entry, index) => {
      const playerId = normalizeId(entry);
      const resolved = rosterLookup.get(playerId);
      if (resolved) return resolved;
      const hydrated = hydratedDirectory.get(playerId);
      return {
        id: playerId,
        name: hydrated?.name || `Player ${index + 1}`,
        role: '',
      };
    });
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfigForm((prev) => {
      if (field === 'streamingMode') {
        const nextMode = normalizeStreamingMode(value);
        const nextProvider = normalizeStreamProvider(prev.streamProvider);
        return {
          ...prev,
          streamProvider:
            nextMode === STREAMING_MODES.DIRECT_MOBILE
              ? (nextProvider === STREAM_PROVIDERS.FACEBOOK ? STREAM_PROVIDERS.FACEBOOK : STREAM_PROVIDERS.YOUTUBE)
              : nextProvider,
          streamingConfig: { ...prev.streamingConfig, mode: nextMode },
        };
      }
      if (field === 'streamProvider') return { ...prev, streamProvider: normalizeStreamProvider(value) };
      if (field === 'youtubeIngestUrl') return { ...prev, streamingConfig: { ...prev.streamingConfig, youtube: { ...prev.streamingConfig.youtube, ingestUrl: value } } };
      if (field === 'livePlaybackUrl') return { ...prev, streamingConfig: { ...prev.streamingConfig, youtube: { ...prev.streamingConfig.youtube, playbackUrl: value }, obs: { ...prev.streamingConfig.obs, playbackUrl: value } } };
      if (field === 'youtubeVideoId') return { ...prev, streamingConfig: { ...prev.streamingConfig, youtube: { ...prev.streamingConfig.youtube, videoId: value } } };
      if (field === 'youtubeLiveLink') return { ...prev, youtubeLiveLink: value, streamUrl: value, streamingConfig: { ...prev.streamingConfig, youtube: { ...prev.streamingConfig.youtube, playbackUrl: value } } };
      if (field === 'rtmpIngestUrl') {
        const existingObsConfig = prev.streamingConfig?.obsConfig || {};
        const cameras = buildObsCameras({
          matchIdentifier: match?.id || match?._id || matchId,
          ingestUrl: value,
          cameraCount: existingObsConfig.cameraCount,
          cameras: existingObsConfig.cameras,
        });
        return {
          ...prev,
          streamingConfig: {
            ...prev.streamingConfig,
            local: { ...prev.streamingConfig.local, ingestUrl: value },
            obs: { ...prev.streamingConfig.obs, ingestUrl: value },
            obsConfig: {
              cameraCount: normalizeCameraCount(existingObsConfig.cameraCount),
              ingestUrl: value,
              cameras,
            },
          },
        };
      }
      if (field === 'rtmpStreamKey') return { ...prev, streamingConfig: { ...prev.streamingConfig, local: { ...prev.streamingConfig.local, streamKey: value }, obs: { ...prev.streamingConfig.obs, streamKey: value } } };
      if (field.startsWith('overEndAds.')) { const key = field.split('.')[1]; const raw = event.target.type === 'checkbox' ? event.target.checked : event.target.value; const parsed = ['durationSeconds','startAfterOver'].includes(key) ? Number(raw) : raw; const next = normalizeOverEndAds({ ...(prev.streamingConfig?.overEndAds || DEFAULT_OVER_END_ADS), [key]: parsed }); if (key === 'enabled' && !raw) next.mode = 'off'; if (key === 'mode') { next.enabled = raw !== 'off'; next.allowDefaultAds = ['default_only','custom_then_default'].includes(raw); } return { ...prev, streamingConfig: { ...prev.streamingConfig, overEndAds: next } }; }
      if (field === 'ballMaterial') return { ...prev, ball: { ...(prev.ball || {}), material: value } };
      if (field === 'ballColor') return { ...prev, ball: { ...(prev.ball || {}), color: value } };
      return { ...prev, [field]: value };
    });
  };

  const setPlayerAnnouncementStatus = (teamPrefix, playerId, status) => {
    setConfigForm((prev) => {
      const playingKey = `${teamPrefix}PlayingPlayers`;
      const substituteKey = `${teamPrefix}SubstitutePlayers`;
      const captainKey = `${teamPrefix}Captain`;
      const viceCaptainKey = `${teamPrefix}ViceCaptain`;
      const wicketKeeperKey = `${teamPrefix}WicketKeeper`;
      const currentPlaying = Array.isArray(prev[playingKey]) ? prev[playingKey] : [];
      const currentSubstitutes = Array.isArray(prev[substituteKey]) ? prev[substituteKey] : [];
      const nextPlaying = currentPlaying.filter((id) => id !== playerId);
      const nextSubstitutes = currentSubstitutes.filter((id) => id !== playerId);
      if (status === 'playing') nextPlaying.push(playerId);
      if (status === 'substitute') nextSubstitutes.push(playerId);
      const announcedSet = new Set([...nextPlaying, ...nextSubstitutes]);
      return {
        ...prev,
        [playingKey]: nextPlaying,
        [substituteKey]: nextSubstitutes,
        [captainKey]: announcedSet.has(prev[captainKey]) ? prev[captainKey] : '',
        [viceCaptainKey]: announcedSet.has(prev[viceCaptainKey]) ? prev[viceCaptainKey] : '',
        [wicketKeeperKey]: announcedSet.has(prev[wicketKeeperKey]) ? prev[wicketKeeperKey] : '',
      };
    });
  };


  const handleObsCameraCountChange = (event) => {
    const count = normalizeCameraCount(event.target.value);
    setConfigForm((prev) => {
      const currentObsConfig = prev.streamingConfig?.obsConfig || {};
      const ingestUrl = (currentObsConfig.ingestUrl || prev.streamingConfig?.obs?.ingestUrl || '').trim();
      const cameras = buildObsCameras({
        matchIdentifier: match?.id || match?._id || matchId,
        ingestUrl,
        cameraCount: count,
        cameras: currentObsConfig.cameras,
      });
      return {
        ...prev,
        streamingConfig: {
          ...prev.streamingConfig,
          obsConfig: {
            cameraCount: count,
            ingestUrl,
            cameras,
          },
        },
      };
    });
  };

  const handleScorerChange = (event) => {
    const nextScorer = event.target.value;
    setConfigForm((prev) => ({
      ...prev,
      scorer: nextScorer,
      umpire1: nextScorer && prev.umpire1 === nextScorer ? '' : prev.umpire1,
      umpire2: nextScorer && prev.umpire2 === nextScorer ? '' : prev.umpire2,
    }));
  };

  const handleUmpireChange = (field) => (event) => {
    const nextUmpire = event.target.value;
    setConfigForm((prev) => ({
      ...prev,
      [field]: nextUmpire,
      scorer: nextUmpire && prev.scorer === nextUmpire ? '' : prev.scorer,
      ...(nextUmpire &&
      ((field === 'umpire1' && prev.umpire2 === nextUmpire) ||
        (field === 'umpire2' && prev.umpire1 === nextUmpire))
        ? { [field === 'umpire1' ? 'umpire2' : 'umpire1']: '' }
        : {}),
    }));
  };

  const [scorers, umpires, matchReferees] = useMemo(() => {
    const scorerList = [];
    const umpireList = [];
    const matchRefereeList = [];

    officials.forEach((official) => {
      const roleSource = Array.isArray(official.tournamentRoles) ? official.tournamentRoles : (Array.isArray(official.teamRoles) ? official.teamRoles : [official.teamRoles].filter(Boolean));
      const roles = new Set(roleSource.map((role) => normalizeTournamentRoleKey(role)));
      if (roles.has('scorer')) {
        scorerList.push(official);
      }
      if (roles.has('umpire')) {
        umpireList.push(official);
      }
      if (roles.has('match_referee')) {
        matchRefereeList.push(official);
      }
    });

    return [scorerList, umpireList, matchRefereeList];
  }, [officials]);

  const filteredScorers = useMemo(() => {
    const blocked = new Set([configForm.umpire1, configForm.umpire2].filter(Boolean));
    return scorers.filter((scorer) => !blocked.has(scorer.name));
  }, [configForm.umpire1, configForm.umpire2, scorers]);

  const filteredUmpires = useMemo(() => {
    if (!configForm.scorer) return umpires;
    return umpires.filter((umpire) => umpire.name !== configForm.scorer);
  }, [configForm.scorer, umpires]);

  const umpireOptions = (excluded) => filteredUmpires.filter((umpire) => umpire.name !== excluded);

  const playersPerSide = Number(configForm.playersPerSide) || 0;
  const squadSize = Number(configForm.squadSize) || playersPerSide || 0;
  const getSelectablePlayerIds = (players) => players.map((player) => player.id);
  const team1AutoSquadIds = useMemo(
    () => getSelectablePlayerIds(teamPlayers.team1),
    [teamPlayers.team1],
  );
  const team2AutoSquadIds = useMemo(
    () => getSelectablePlayerIds(teamPlayers.team2),
    [teamPlayers.team2],
  );
  const team1SelectedRoster = useMemo(
    () => (team1AutoSquadIds.length
      ? resolveRosterByIds({
        rosterIds: team1AutoSquadIds,
        playerDirectory: buildPlayerDirectory({ players: teamPlayers.team1 }),
        matchId: match?.id || match?._id || matchId,
      })
      : buildSelectedRoster(configForm.team1SquadPlayers || configForm.team1Players || [], teamPlayers.team1)),
    [buildSelectedRoster, configForm.team1Players, configForm.team1SquadPlayers, match?._id, match?.id, matchId, team1AutoSquadIds, teamPlayers.team1],
  );
  const team2SelectedRoster = useMemo(
    () => (team2AutoSquadIds.length
      ? resolveRosterByIds({
        rosterIds: team2AutoSquadIds,
        playerDirectory: buildPlayerDirectory({ players: teamPlayers.team2 }),
        matchId: match?.id || match?._id || matchId,
      })
      : buildSelectedRoster(configForm.team2SquadPlayers || configForm.team2Players || [], teamPlayers.team2)),
    [buildSelectedRoster, configForm.team2Players, configForm.team2SquadPlayers, match?._id, match?.id, matchId, team2AutoSquadIds, teamPlayers.team2],
  );
  const team1AnnouncedIds = useMemo(
    () => uniqueById([...(configForm.team1PlayingPlayers || []), ...(configForm.team1SubstitutePlayers || [])]),
    [configForm.team1PlayingPlayers, configForm.team1SubstitutePlayers],
  );
  const team2AnnouncedIds = useMemo(
    () => uniqueById([...(configForm.team2PlayingPlayers || []), ...(configForm.team2SubstitutePlayers || [])]),
    [configForm.team2PlayingPlayers, configForm.team2SubstitutePlayers],
  );
  const team1AnnouncedRoster = useMemo(
    () => buildSelectedRoster(team1AnnouncedIds, team1SelectedRoster),
    [buildSelectedRoster, team1AnnouncedIds, team1SelectedRoster],
  );
  const team2AnnouncedRoster = useMemo(
    () => buildSelectedRoster(team2AnnouncedIds, team2SelectedRoster),
    [buildSelectedRoster, team2AnnouncedIds, team2SelectedRoster],
  );

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
  }, [
    match?._id,
    match?.configuration?.playerDirectory,
    match?.configuration?.team1Players,
    match?.configuration?.team1PlayersData,
    match?.configuration?.team2Players,
    match?.configuration?.team2PlayersData,
    match?.id,
    team1SelectedRoster?.length,
    team2SelectedRoster?.length,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const team1ActiveCount = Array.isArray(configForm.team1PlayingPlayers)
      ? configForm.team1PlayingPlayers.length
      : 0;
    const team2ActiveCount = Array.isArray(configForm.team2PlayingPlayers)
      ? configForm.team2PlayingPlayers.length
      : 0;
    // eslint-disable-next-line no-console
    console.debug('[match-config][squad-vs-active]', {
      team1: {
        squadPlayersCount: team1SelectedRoster.length,
        activePlayersCount: team1ActiveCount,
        captainOptionsCount: team1SelectedRoster.length,
        viceCaptainOptionsCount: team1SelectedRoster.length,
        wicketKeeperOptionsCount: team1SelectedRoster.length,
      },
      team2: {
        squadPlayersCount: team2SelectedRoster.length,
        activePlayersCount: team2ActiveCount,
        captainOptionsCount: team2SelectedRoster.length,
        viceCaptainOptionsCount: team2SelectedRoster.length,
        wicketKeeperOptionsCount: team2SelectedRoster.length,
      },
    });
  }, [configForm.team1PlayingPlayers, configForm.team2PlayingPlayers, team1SelectedRoster.length, team2SelectedRoster.length]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
  }, [configForm.playersPerSide, configForm.playingCount, team1SelectedRoster.length, team2SelectedRoster.length]);


  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token || !tournamentId || !matchId) {
      setErrorMessage('Save the schedule before configuring matches.');
      return;
    }

    if (configForm.umpire1 && configForm.umpire2 && configForm.umpire1 === configForm.umpire2) {
      setErrorMessage('Umpire 1 and Umpire 2 cannot be the same.');
      return;
    }

    const payloadStreamingMode = normalizeStreamingMode(configForm.streamingConfig?.mode);
    const obsIngestUrl = (configForm.streamingConfig?.obs?.ingestUrl || configForm.streamingConfig?.local?.ingestUrl || '').trim();
    const existingObsConfig = configForm.streamingConfig?.obsConfig || {};
    const normalizedObsCameraCount = normalizeCameraCount(existingObsConfig.cameraCount);
    const normalizedObsCameras = buildObsCameras({
      matchIdentifier: match?.id || match?._id || matchId,
      ingestUrl: obsIngestUrl,
      cameraCount: normalizedObsCameraCount,
      cameras: existingObsConfig.cameras,
    });
    if (payloadStreamingMode === STREAMING_MODES.OBS) {
      if (!obsIngestUrl) {
        setErrorMessage('RTMP Ingest URL is required for OBS mode.');
        return;
      }
      if (!normalizedObsCameras.length || normalizedObsCameras.some((camera) => !camera.streamKey)) {
        setErrorMessage('Each OBS camera feed must have a stream key.');
        return;
      }
    }

    if (playersPerSide && squadSize && squadSize < playersPerSide) {
      setErrorMessage('Squad size must be greater than or equal to playing players per side.');
      return;
    }

    if (
      playersPerSide &&
      ((team1AutoSquadIds.length > 0 && team1AutoSquadIds.length < playersPerSide)
        || (team2AutoSquadIds.length > 0 && team2AutoSquadIds.length < playersPerSide))
    ) {
      setErrorMessage('Each team must have enough players to satisfy Playing Players Per Side.');
      return;
    }

    setIsSaving(true);
    setErrorMessage('');

    try {
      const resolvedYouTubeLiveLink = (configForm.youtubeLiveLink || configForm.streamUrl || configForm.streamingConfig?.youtube?.playbackUrl || '').trim();
      const obsPlaybackUrl = (configForm.streamingConfig?.obs?.playbackUrl || '').trim();
      const payloadStreamProvider = normalizeStreamProvider(configForm.streamProvider);
      const shouldIncludeProvider = payloadStreamingMode === STREAMING_MODES.DIRECT_MOBILE;
      const shouldIncludeYouTubeLink = shouldIncludeProvider && payloadStreamProvider === STREAM_PROVIDERS.YOUTUBE;
      const shouldIncludeObsFields = payloadStreamingMode === STREAMING_MODES.OBS;
      const primaryObsStreamKey = normalizedObsCameras[0]?.streamKey || '';
      const resolvedTeam1SquadPlayers = team1AutoSquadIds.length
        ? team1AutoSquadIds
        : (configForm.team1SquadPlayers || configForm.team1Players || []);
      const resolvedTeam2SquadPlayers = team2AutoSquadIds.length
        ? team2AutoSquadIds
        : (configForm.team2SquadPlayers || configForm.team2Players || []);
      const resolvedTeam1PlayingPlayers = (configForm.team1PlayingPlayers || []).filter((id) => resolvedTeam1SquadPlayers.includes(id));
      const resolvedTeam2PlayingPlayers = (configForm.team2PlayingPlayers || []).filter((id) => resolvedTeam2SquadPlayers.includes(id));
      const resolvedTeam1SubstitutePlayers = (configForm.team1SubstitutePlayers || [])
        .filter((id) => resolvedTeam1SquadPlayers.includes(id) && !resolvedTeam1PlayingPlayers.includes(id));
      const resolvedTeam2SubstitutePlayers = (configForm.team2SubstitutePlayers || [])
        .filter((id) => resolvedTeam2SquadPlayers.includes(id) && !resolvedTeam2PlayingPlayers.includes(id));
      const payload = {
        ...configForm,
        team1Players: resolvedTeam1SquadPlayers,
        team2Players: resolvedTeam2SquadPlayers,
        team1SquadPlayers: resolvedTeam1SquadPlayers,
        team2SquadPlayers: resolvedTeam2SquadPlayers,
        team1PlayingPlayers: resolvedTeam1PlayingPlayers,
        team2PlayingPlayers: resolvedTeam2PlayingPlayers,
        team1SubstitutePlayers: resolvedTeam1SubstitutePlayers,
        team2SubstitutePlayers: resolvedTeam2SubstitutePlayers,
        streamingMode: payloadStreamingMode,
        streamProvider: shouldIncludeProvider ? payloadStreamProvider : '',
        youtubeLiveLink: shouldIncludeYouTubeLink ? resolvedYouTubeLiveLink : '',
        streamUrl: shouldIncludeYouTubeLink ? resolvedYouTubeLiveLink : (shouldIncludeObsFields ? obsPlaybackUrl : ''),
        playbackUrl: shouldIncludeObsFields ? obsPlaybackUrl : '',
        livePlaybackUrl: shouldIncludeYouTubeLink ? resolvedYouTubeLiveLink : (shouldIncludeObsFields ? obsPlaybackUrl : ''),
        rtmpIngestUrl: shouldIncludeObsFields ? obsIngestUrl : '',
        rtmpStreamKey: shouldIncludeObsFields ? primaryObsStreamKey : '',
        streamingConfig: {
          ...configForm.streamingConfig,
          mode: toLegacyStreamingMode(payloadStreamingMode),
          overlayMode: 'burn_in',
          overEndAds: normalizeOverEndAds(configForm.streamingConfig?.overEndAds),
          youtube: {
            ...configForm.streamingConfig?.youtube,
            playbackUrl: shouldIncludeYouTubeLink ? resolvedYouTubeLiveLink : '',
          },
          obs: {
            ...configForm.streamingConfig?.obs,
            ingestUrl: shouldIncludeObsFields ? obsIngestUrl : (configForm.streamingConfig?.obs?.ingestUrl || ''),
            streamKey: shouldIncludeObsFields ? primaryObsStreamKey : (configForm.streamingConfig?.obs?.streamKey || ''),
            playbackUrl: shouldIncludeObsFields ? obsPlaybackUrl : '',
          },
          obsConfig: shouldIncludeObsFields
            ? {
              cameraCount: normalizedObsCameraCount,
              ingestUrl: obsIngestUrl,
              cameras: normalizedObsCameras,
            }
            : (configForm.streamingConfig?.obsConfig || {}),
        },
        obsConfig: shouldIncludeObsFields
          ? {
            cameraCount: normalizedObsCameraCount,
            ingestUrl: obsIngestUrl,
            cameras: normalizedObsCameras,
          }
          : undefined,
        overlayMode: 'burn_in',
        overEndAds: normalizeOverEndAds(configForm.streamingConfig?.overEndAds),
      };
      const response = await updateMatchConfiguration({
        token,
        tournamentId,
        matchId,
        payload,
      });
      if (response?.match) {
        setMatch(response.match);
        setConfigForm(buildInitialConfig(response.match));
      }
      setSavedMessage('Saved ✓');
      setTimeout(() => setSavedMessage(''), 2000);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to update match configuration.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicViewLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(publicViewLink);
      setCopyStatus('Public link copied.');
    } catch (error) {
      setCopyStatus('Unable to copy the public link.');
    }
  };


  const copyText = async (value, successMessage) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus(successMessage);
    } catch (_error) {
      setCopyStatus('Unable to copy value.');
    }
  };


  const copyObsCameraField = async (camera = {}, field = 'streamKey') => {
    const value = field === 'ingestUrl' ? camera?.ingestUrl : camera?.streamKey;
    const label = camera?.label || 'Camera';
    await copyText(value, `Copied ${label} ${field === 'ingestUrl' ? 'ingest URL' : 'stream key'}.`);
  };

  const loadLocalPreviewConfig = async () => {
    try {
      setLocalPreviewStatus('Loading local preview details...');
      const response = await fetchLocalStreamPreview();
      if (!response?.enabled) {
        setLocalPreviewConfig(null);
        setLocalPreviewStatus('Local preview disabled on server');
        return;
      }
      setLocalPreviewConfig(response);
      setLocalPreviewStatus('Local preview configuration loaded.');
    } catch (error) {
      setLocalPreviewConfig(null);
      setLocalPreviewStatus(error.message || 'Unable to load local preview configuration.');
    }
  };

  const handleToggleLocalPreview = async () => {
    const nextValue = !localPreviewEnabled;
    setLocalPreviewEnabled(nextValue);
    if (!nextValue) {
      setLocalPreviewConfig(null);
      setLocalPreviewStatus('');
      return;
    }

    await loadLocalPreviewConfig();
  };


  const handleStartRelay = async () => {
    if (!token || !matchId || !tournamentId) return;
    try {
      const data = await startRelay({ token, matchId, tournamentId });
      setRelayStatus(data?.relayStatus || null);
      setLocalPreviewStatus('Relay started.');
    } catch (error) {
      setLocalPreviewStatus(error.message || 'Unable to start relay.');
    }
  };

  const handleStopRelay = async () => {
    if (!token || !matchId || !tournamentId) return;
    try {
      const data = await stopRelay({ token, matchId, tournamentId });
      setRelayStatus(data?.relayStatus || null);
      setLocalPreviewStatus('Relay stopped.');
    } catch (error) {
      setLocalPreviewStatus(error.message || 'Unable to stop relay.');
    }
  };

  const handleGeneratePublicStreamUrl = async () => {
    if (!token || !tournamentId || !matchId) {
      setErrorMessage('Save the schedule before generating YouTube details.');
      return;
    }

    setIsGeneratingYouTube(true);
    setStreamUrlStatus('Generating YouTube live details...');
    setErrorMessage('');

    try {
      const response = await generateMatchYouTubeStream({ token, tournamentId, matchId });
      const resolvedLink = resolveYouTubeLiveLinkFromResponse(response);
      const fallbackLink = resolveYouTubePublicStreamUrl(
        response?.youtubeStreamKey || configForm.streamingConfig?.youtube?.streamKey,
        response?.rtmpStreamKey || configForm.streamingConfig?.local?.streamKey,
      );
      const liveLink = resolvedLink || fallbackLink;

      if (!liveLink) {
        throw new Error('YouTube stream generated, but no public live link was returned.');
      }

      const previousWatchUrl = configForm.youtubeLiveLink || configForm.streamingConfig?.youtube?.playbackUrl || '';

      setConfigForm((prev) => ({
        ...prev,
        youtubeLiveLink: liveLink,
        streamUrl: liveLink,
        streamingConfig: {
          ...prev.streamingConfig,
          youtube: {
            ...prev.streamingConfig.youtube,
            playbackUrl: liveLink,
            streamKey: response?.youtubeStreamKey || prev.streamingConfig.youtube?.streamKey || '',
            ingestUrl: response?.youtubeIngestUrl || prev.streamingConfig.youtube?.ingestUrl || '',
            videoId: response?.youtubeVideoId || prev.streamingConfig.youtube?.videoId || '',
          },
        },
      }));

      const [freshConfig, freshStreamDetails] = await Promise.allSettled([
        fetchMatchConfiguration({ token, tournamentId, matchId }),
        fetchMatchStreamDetails({ token, matchId, tournamentId }),
      ]);

      if (freshConfig.status === 'fulfilled') {
        const payload =
          freshConfig.value?.configuration || freshConfig.value?.streamDetails || freshConfig.value || null;
        if (payload) {
          setConfigForm((prev) => {
            const nextFromServer = buildInitialConfig(payload);
            return {
              ...prev,
              ...nextFromServer,
            };
          });
        }
      }

      if (freshStreamDetails.status === 'fulfilled') {
        setStreamDetails(freshStreamDetails.value || null);
      }

      setStreamUrlStatus('YouTube live link generated and filled automatically.');
    } catch (error) {
      setErrorMessage(error.message || 'Unable to generate YouTube stream details.');
      setStreamUrlStatus('');
    } finally {
      setIsGeneratingYouTube(false);
    }
  };


  return (
    <PageShell containerClassName="p-0">
      <div className="py-4">
      <div className="mb-4 d-flex flex-column gap-2">
        <p className="eyebrow text-uppercase text-primary fw-semibold mb-0">Match configuration</p>
        <h1 className="h3 mb-1">Configure match details</h1>
        <p className="text-muted mb-0">
          Assign officials and rules for this fixture before match day.
        </p>
        <div className="d-flex flex-wrap gap-2">
          <Link
            className="btn btn-sm btn-outline-secondary"
            to={
              communityId
                ? `/community/${communityId}/match-manager/${managerId}/tournaments/${tournamentId}`
                : clubId
                  ? `/club/${clubId}/match-manager/${managerId}/tournaments/${tournamentId}`
                  : individualTournamentId
                    ? `/individual-tournament/${individualTournamentId}/match-manager/${managerId}/tournaments/${tournamentId}`
                    : `/org/${organizationId}/match-manager/${managerId}/tournaments/${tournamentId}`
            }
          >
            Back to match schedule
          </Link>
        </div>
      </div>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}


      {savedMessage && (
        <div className="alert alert-success" role="status">
          {savedMessage}
        </div>
      )}

      {isLoading && (
        <div className="alert alert-secondary" role="alert">
          Loading match details...
        </div>
      )}

      {tournament && match && (
        <div className="alert alert-info" role="alert">
          <div>{tournament.name} • {matchLabel}</div>
          <div className="d-flex align-items-center gap-3 mt-2">
            <div className="d-flex align-items-center gap-2">
              {match.team1Logo ? <img src={match.team1Logo} alt={match.team1 || 'Team 1'} className="rounded border" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : null}
              <span>{match.team1 || 'Team 1'}</span>
            </div>
            <span className="text-muted">vs</span>
            <div className="d-flex align-items-center gap-2">
              {match.team2Logo ? <img src={match.team2Logo} alt={match.team2 || 'Team 2'} className="rounded border" style={{ width: 28, height: 28, objectFit: 'cover' }} /> : null}
              <span>{match.team2 || 'Team 2'}</span>
            </div>
          </div>
        </div>
      )}

      {match && (
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body">
            <label className="form-label">Public match link</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={publicViewLink}
                readOnly
              />
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={handleCopyLink}
                disabled={!publicViewLink}
              >
                Copy link
              </button>
            </div>
            {copyStatus ? <p className="text-muted small mt-2 mb-0">{copyStatus}</p> : null}
          </div>
        </div>
      )}


      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h6 mb-0">Live stream status</h2>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={async () => {
              try {
                const statusPayload = await fetchMatchLiveStatus({ token, matchId, tournamentId });
                setLiveStatus(statusPayload || null);
                setLiveStatusError('');
              } catch (error) {
                setLiveStatusError(error?.message || 'Unable to load live status.');
              }
            }}>Refresh</button>
          </div>
          <div><small className="text-muted">Streaming mode</small><br /><strong>{liveStatusMode}</strong></div>
          <div><small className="text-muted">Provider</small><br /><strong>{liveStatusProvider}</strong></div>
          <div>
            <small className="text-muted">Live status</small><br />
            <span className={`badge ${liveHealthBadge.className}`}>{liveHealthBadge.label}</span>
          </div>
          <div><small className="text-muted">YouTube ingest status</small><br /><strong>{liveStreamStatusLabel}</strong></div>
          <div><small className="text-muted">YouTube broadcast status</small><br /><strong>{liveStatus?.youtubeStreamStatus || 'unknown'}</strong></div>
          {typeof liveStatus?.concurrentViewers === 'number' ? <div><small className="text-muted">Concurrent viewers</small><br /><strong>{liveStatus.concurrentViewers}</strong></div> : null}
          <div><small className="text-muted">Playback URL</small><br />{livePlaybackUrl ? <code>{livePlaybackUrl}</code> : <span className="text-muted">Not available yet</span>}</div>
          {livePlaybackUrl ? <div><a className="btn btn-sm btn-outline-primary" href={livePlaybackUrl} target="_blank" rel="noreferrer">Watch live</a></div> : null}
          {liveStatusError ? <p className="text-danger small mb-0">{liveStatusError}</p> : null}
          {liveStatus?.isLive ? <p className="text-muted small mb-0">Auto-refreshing every 5s while live.</p> : null}
        </div>
      </div>

      <form className="card shadow-sm border-0" onSubmit={handleSubmit}>
        <div className="card-body d-flex flex-column gap-3">
          {(playersError || (match && !teamPlayers.team1.length && !teamPlayers.team2.length)) && (
            <div className="alert alert-warning mb-0" role="alert">
              {playersError || 'Team players are not available yet. Add players to teams first.'}
            </div>
          )}
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Venue</label>
              <input
                type="text"
                className="form-control"
                value={configForm.venue}
                onChange={handleChange('venue')}
                placeholder="Main ground"
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Notes</label>
              <input
                type="text"
                className="form-control"
                value={configForm.notes}
                onChange={handleChange('notes')}
                placeholder="Warmup starts 30 mins before"
              />
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label">Scorer</label>
              <select className="form-select" value={configForm.scorer} onChange={handleScorerChange}>
                <option value="">Select scorer</option>
                {filteredScorers.map((scorer) => (
                  <option key={scorer.id} value={scorer.name}>
                    {scorer.name}
                    {scorer.email ? ` (${scorer.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Umpire 1</label>
              <select className="form-select" value={configForm.umpire1} onChange={handleUmpireChange('umpire1')}>
                <option value="">Select umpire</option>
                {umpireOptions(configForm.umpire2).map((umpire) => (
                  <option key={umpire.id} value={umpire.name}>
                    {umpire.name}
                    {umpire.email ? ` (${umpire.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Umpire 2</label>
              <select className="form-select" value={configForm.umpire2} onChange={handleUmpireChange('umpire2')}>
                <option value="">Select umpire</option>
                {umpireOptions(configForm.umpire1).map((umpire) => (
                  <option key={umpire.id} value={umpire.name}>
                    {umpire.name}
                    {umpire.email ? ` (${umpire.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {officialsError && (
            <div className="alert alert-warning mb-0" role="alert">
              {officialsError}
            </div>
          )}

          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Match referee</label>
              <select
                className="form-select"
                value={configForm.matchReferee}
                onChange={handleChange('matchReferee')}
              >
                <option value="">Select match referee</option>
                {matchReferees.map((referee) => (
                  <option key={referee.id} value={referee.name}>
                    {referee.name}
                    {referee.email ? ` (${referee.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border rounded-3 p-3">
            <div className="d-flex flex-column gap-1 mb-3">
              <h2 className="h6 mb-0">Live streaming</h2>
              <p className="text-muted small mb-0">
                Provide the watch link and stream credentials for this match broadcast.
              </p>
            </div>
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <label className="form-label">Streaming Mode</label>
                <select className="form-select" value={streamingMode} onChange={handleChange('streamingMode')}>
                  <option value={STREAMING_MODES.DIRECT_MOBILE}>{STREAMING_MODES.DIRECT_MOBILE}</option>
                  <option value={STREAMING_MODES.OBS}>{STREAMING_MODES.OBS}</option>
                  {isLocalDev ? <option value={STREAMING_MODES.LOCAL_MAC_TESTING}>{STREAMING_MODES.LOCAL_MAC_TESTING}</option> : null}
                </select>
              </div>

              {isDirectMode ? (
                <div className="col-12 col-lg-6">
                  <label className="form-label">Stream Provider</label>
                  <select className="form-select" value={selectedStreamProvider} onChange={handleChange('streamProvider')}>
                    <option value={STREAM_PROVIDERS.YOUTUBE}>{STREAM_PROVIDERS.YOUTUBE}</option>
                    <option value={STREAM_PROVIDERS.FACEBOOK}>{STREAM_PROVIDERS.FACEBOOK}</option>
                  </select>
                </div>
              ) : null}

              {isDirectMode && isYouTubeProvider ? (
                <div className="col-12 col-lg-6">
                  <label className="form-label">YouTube Live Link</label>
                  <input
                    type="url"
                    className="form-control"
                    value={configForm.youtubeLiveLink || ''}
                    onChange={handleChange('youtubeLiveLink')}
                    placeholder="Will appear after Generate YouTube"
                  />
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleGeneratePublicStreamUrl}
                      disabled={isGeneratingYouTube}
                    >
                      Generate YouTube
                      {isGeneratingYouTube ? (<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />) : null}
                    </button>
                  </div>
                  {configForm.youtubeLiveLink ? (
                    <div className="mt-2 d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-2">
                        <a href={configForm.youtubeLiveLink} target="_blank" rel="noreferrer" className="small">Open Live Link</a>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => copyText(configForm.youtubeLiveLink, 'Copied YouTube live link.')}
                        >
                          Copy
                        </button>
                      </div>
                      <small className="text-muted text-break">{configForm.youtubeLiveLink}</small>
                    </div>
                  ) : (
                    <small className="text-muted d-block mt-2">Will appear after Generate YouTube</small>
                  )}
                  {streamUrlStatus ? <p className="text-muted small mt-2 mb-0">{streamUrlStatus}</p> : null}
                </div>
              ) : null}

              {isObsMode ? (
                <>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Playback URL</label>
                    <input
                      type="url"
                      className="form-control"
                      value={configForm.streamingConfig?.obs?.playbackUrl || ''}
                      onChange={handleChange('livePlaybackUrl')}
                      placeholder="https://example.com/watch"
                    />
                    <small className="text-muted d-block mt-1">Optional public playback/watch URL</small>
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">RTMP Ingest URL</label>
                    <div className="input-group">
                      <input
                        type="url"
                        className="form-control"
                        value={configForm.streamingConfig?.obs?.ingestUrl || ''}
                        onChange={handleChange('rtmpIngestUrl')}
                        placeholder="rtmp://<server>/live"
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => copyText(configForm.streamingConfig?.obs?.ingestUrl, 'Copied RTMP ingest URL.')}
                        disabled={!configForm.streamingConfig?.obs?.ingestUrl}
                      >
                        Copy
                      </button>
                    </div>
                    <small className="text-muted d-block mt-1">Common ingest URL for all camera feeds</small>
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Camera count</label>
                    <input
                      type="number"
                      min="1"
                      max="6"
                      className="form-control"
                      value={configForm.streamingConfig?.obsConfig?.cameraCount || DEFAULT_OBS_CAMERA_COUNT}
                      onChange={handleObsCameraCountChange}
                    />
                    <small className="text-muted d-block mt-1">Unique stream keys will be generated per camera feed.</small>
                  </div>
                  <div className="col-12">
                    <div className="border rounded p-3">
                      <h6 className="mb-2">OBS Camera Feeds</h6>
                      <div className="small text-muted mb-1">Use these feeds as separate inputs in OBS.</div>
                      <div className="small text-muted mb-2">Each mobile device should stream to one assigned camera feed.</div>
                      <div className="d-flex flex-column gap-2">
                        {(configForm.streamingConfig?.obsConfig?.cameras || []).map((camera, index) => (
                          <div key={`obs-camera-${index}`} className="border rounded p-2">
                            <div className="fw-semibold mb-2">{camera.label}</div>
                            <div className="mb-1"><small className="text-muted">Camera ID</small><br /><code>{camera.id || `cam${index + 1}`}</code></div>
                            <div className="mb-1"><small className="text-muted">Ingest URL</small><br /><code>{camera.ingestUrl || '—'}</code></div>
                            <div className="mb-1"><small className="text-muted">Stream Key</small><br /><code>{camera.streamKey || '—'}</code></div>
                            <div><small className="text-muted">Publish URL</small><br /><code>{camera.publishUrl || (camera.ingestUrl && camera.streamKey ? `${camera.ingestUrl}/${camera.streamKey}` : '—')}</code></div>
                            <div className="d-flex gap-2 mt-2">
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyObsCameraField(camera, 'ingestUrl')} disabled={!camera.ingestUrl}>Copy URL</button>
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyObsCameraField(camera, 'streamKey')} disabled={!camera.streamKey}>Copy key</button>
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => copyText(camera.publishUrl || (camera.ingestUrl && camera.streamKey ? `${camera.ingestUrl}/${camera.streamKey}` : ''), `Copied ${camera.label || 'Camera'} publish URL.`)} disabled={!(camera.publishUrl || (camera.ingestUrl && camera.streamKey))}>Copy publish URL</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              {isObsMode ? (
                <div className="col-12">
                  <div className="border rounded p-3 bg-light">
                    <h6 className="mb-2">OBS Settings</h6>
                    <div><small className="text-muted">OBS ingest URL</small><br /><code>{streamDetails?.obs?.ingestUrl || configForm.streamingConfig?.obs?.ingestUrl || '—'}</code></div>
                    <div><small className="text-muted">Primary OBS key</small><br /><code>{streamDetails?.obs?.streamKey || configForm.streamingConfig?.obsConfig?.cameras?.[0]?.streamKey || configForm.streamingConfig?.obs?.streamKey || '—'}</code></div>
                    <hr className="my-2" />
                    <div><small className="text-muted">YouTube ingest URL</small><br /><code>{obsYouTubeIngestUrl || '—'}</code></div>
                    <div><small className="text-muted">YouTube stream key</small><br /><code>{obsYouTubeStreamKey || '—'}</code></div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleGeneratePublicStreamUrl}
                        disabled={isGeneratingYouTube}
                      >
                        Generate YouTube
                        {isGeneratingYouTube ? (<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />) : null}
                      </button>
                      {livePlaybackUrl ? <a className="btn btn-sm btn-outline-primary" href={livePlaybackUrl} target="_blank" rel="noreferrer">Watch live</a> : null}
                    </div>
                    <div className="small text-muted mt-2">OBS can publish to this match-specific YouTube event via relay.</div>
                    <div className="small mt-2">Relay running: <strong>{relayStatus?.running ? 'Yes' : 'No'}</strong></div>
                    {relayStatus?.lastError ? <div className="small text-danger mt-1">Last error: {relayStatus.lastError}</div> : null}
                    <div className="d-flex gap-2 mt-2">
                      <button type="button" className="btn btn-sm btn-outline-success" onClick={handleStartRelay}>Start relay</button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleStopRelay}>Stop relay</button>
                    </div>
                  </div>
                </div>
              ) : null}


              <div className="col-12">
                <div className="border rounded p-3 bg-light">
                  <h6 className="mb-2">Over-end ad settings</h6>
                  <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" role="switch" id="overEndAdsEnabled" checked={configForm.streamingConfig?.overEndAds?.enabled !== false && configForm.streamingConfig?.overEndAds?.mode !== 'off'} onChange={handleChange('overEndAds.enabled')} />
                    <label className="form-check-label" htmlFor="overEndAdsEnabled">Enable over-end ads</label>
                  </div>
                  <div className="row g-3">
                    <div className="col-12 col-md-4"><label className="form-label" htmlFor="overEndAdsMode">Mode</label><select id="overEndAdsMode" className="form-select" value={configForm.streamingConfig?.overEndAds?.mode || DEFAULT_OVER_END_ADS.mode} onChange={handleChange('overEndAds.mode')}>{OVER_END_AD_MODES.map((mode) => <option key={mode} value={mode}>{mode}</option>)}</select></div>
                    <div className="col-12 col-md-4"><label className="form-label" htmlFor="overEndAdsDurationSeconds">Duration seconds</label><input id="overEndAdsDurationSeconds" type="number" min="1" className="form-control" value={configForm.streamingConfig?.overEndAds?.durationSeconds || DEFAULT_OVER_END_ADS.durationSeconds} onChange={handleChange('overEndAds.durationSeconds')} /></div>
                    <div className="col-12 col-md-4"><label className="form-label" htmlFor="overEndAdsFrequency">Frequency</label><select id="overEndAdsFrequency" className="form-select" value={configForm.streamingConfig?.overEndAds?.frequency || DEFAULT_OVER_END_ADS.frequency} onChange={handleChange('overEndAds.frequency')}>{OVER_END_AD_FREQUENCIES.map((frequency) => <option key={frequency} value={frequency}>{frequency}</option>)}</select></div>
                    <div className="col-12 col-md-4"><label className="form-label" htmlFor="overEndAdsStartAfterOver">Start after over</label><input id="overEndAdsStartAfterOver" type="number" min="1" className="form-control" value={configForm.streamingConfig?.overEndAds?.startAfterOver || DEFAULT_OVER_END_ADS.startAfterOver} onChange={handleChange('overEndAds.startAfterOver')} /></div>
                    <div className="col-12 col-md-4 d-flex align-items-end"><div className="form-check"><input className="form-check-input" type="checkbox" id="showScoreBugDuringAd" checked={Boolean(configForm.streamingConfig?.overEndAds?.showScoreBugDuringAd)} onChange={handleChange('overEndAds.showScoreBugDuringAd')} /><label className="form-check-label" htmlFor="showScoreBugDuringAd">Show score bug during ad</label></div></div>
                  </div>
                  <small className="text-muted d-block mt-2">Default CrickArena fallback is enabled for default_only and custom_then_default modes.</small>
                </div>
              </div>

              {isLocalMode ? (
                <>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="localPreviewToggle"
                        checked={localPreviewEnabled}
                        onChange={handleToggleLocalPreview}
                      />
                      <label className="form-check-label" htmlFor="localPreviewToggle">
                        Local Preview
                      </label>
                    </div>
                    {localPreviewStatus ? <p className="text-muted small mt-2 mb-0">{localPreviewStatus}</p> : null}
                    {localPreviewEnabled && localPreviewConfig ? (
                      <div className="border rounded p-3 mt-2 d-flex flex-column gap-2">
                        <div>
                          <small className="text-muted d-block">RTMP URL</small>
                          <code>{localPreviewConfig.rtmpUrl}</code>
                          <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => copyText(localPreviewConfig.rtmpUrl, 'Copied RTMP URL.')}>Copy</button>
                        </div>
                        <div>
                          <small className="text-muted d-block">Playback URL</small>
                          <code>{localPreviewConfig.playUrlRtmp}</code>
                          <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={() => copyText(localPreviewConfig.playUrlRtmp, 'Copied playback URL.')}>Copy</button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {streamDetails?.local ? (
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <h6 className="mb-2">Local Preview</h6>
                        <div><small className="text-muted">Local RTMP ingest URL</small><br /><code>{streamDetails.local.ingestUrl}</code></div>
                        <div><small className="text-muted">Local stream key</small><br /><code>{streamDetails.local.streamKey}</code></div>
                        <div><small className="text-muted">ffplay command</small><br /><code>{`ffplay -fflags nobuffer -flags low_delay -framedrop ${streamDetails.local.ffplayUrl}`}</code></div>
                        <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => copyText(`ffplay -fflags nobuffer -flags low_delay -framedrop ${streamDetails.local.ffplayUrl}`, 'Copied ffplay command.')}>Copy ffplay</button>
                      </div>
                    </div>
                  ) : null}
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Local RTMP ingest URL</label>
                    <input
                      type="url"
                      className="form-control"
                      value={configForm.streamingConfig?.local?.ingestUrl || ''}
                      onChange={handleChange('rtmpIngestUrl')}
                      placeholder="rtmp://<LAN_IP>:1935/live"
                    />
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">RTMP stream key</label>
                    <input
                      type="text"
                      className="form-control"
                      value={configForm.streamingConfig?.local?.streamKey || ''}
                      onChange={handleChange('rtmpStreamKey')}
                      placeholder="RTMP stream key"
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label">Overs per innings</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={configForm.overs}
                onChange={handleChange('overs')}
                placeholder="20"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Max overs per player</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={configForm.maxOversPerPlayer}
                onChange={handleChange('maxOversPerPlayer')}
                placeholder="4"
              />
              <div className="form-text">Optional. Leave blank for no per-bowler over limit.</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Max players allowed to bowl full quota</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={configForm.maxPlayersAtMaxOvers}
                onChange={handleChange('maxPlayersAtMaxOvers')}
                placeholder="3"
              />
              <div className="form-text">Example: if max overs per player is 4 and this value is 3, only 3 bowlers can bowl all 4 overs.</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Playing players per side</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={configForm.playersPerSide}
                onChange={handleChange('playersPerSide')}
                placeholder="11"
              />
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Squad size</label>
              <input
                type="number"
                min={playersPerSide || 1}
                className="form-control"
                value={configForm.squadSize}
                onChange={handleChange('squadSize')}
                placeholder={configForm.playersPerSide || '15'}
              />
              <div className="form-text">If left blank, defaults to Playing Players Per Side.</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Ball Material/Type</label>
              <select
                className="form-select"
                value={configForm.ball?.material || 'tennis'}
                onChange={handleChange('ballMaterial')}
                required
              >
                {BALL_MATERIAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label">Ball Color</label>
              <select
                className="form-select"
                value={configForm.ball?.color || 'yellow'}
                onChange={handleChange('ballColor')}
                required
              >
                {BALL_COLOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <label className="form-label d-block">Allow Last Batsman to Play</label>
              <div className="form-check form-switch mt-2">
                <input
                  id="allowLastBatsman"
                  type="checkbox"
                  className="form-check-input"
                  checked={Boolean(configForm.allowLastBatsman)}
                  onChange={handleChange('allowLastBatsman')}
                />
                <label className="form-check-label" htmlFor="allowLastBatsman">
                  Enable last batsman mode
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3" id="roster">
            <div className="col-12 col-lg-6">
              <div className="border rounded-3 p-3 h-100">
                <h3 className="h6 mb-1">{match?.team1 || 'Team 1'} squad</h3>
                <p className="text-muted small mb-2">
                  Auto-selected squad players: {team1SelectedRoster.length}{squadSize ? ` / ${squadSize}` : ''}
                </p>
                <div className="row g-2 mb-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label small">Captain</label>
                    <select
                      className="form-select form-select-sm"
                      value={configForm.team1Captain}
                      disabled={!team1AnnouncedRoster.length}
                      onChange={handleChange('team1Captain')}
                    >
                      <option value="">Select captain</option>
                      {team1AnnouncedRoster.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label small">Vice captain</label>
                    <select
                      className="form-select form-select-sm"
                      value={configForm.team1ViceCaptain}
                      disabled={!team1AnnouncedRoster.length}
                      onChange={handleChange('team1ViceCaptain')}
                    >
                      <option value="">Select vice captain</option>
                      {team1AnnouncedRoster.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label small">Wicket keeper</label>
                    <select
                      className="form-select form-select-sm"
                      value={configForm.team1WicketKeeper}
                      disabled={!team1AnnouncedRoster.length}
                      onChange={handleChange('team1WicketKeeper')}
                    >
                      <option value="">Select wicket keeper</option>
                      {team1AnnouncedRoster.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  {team1SelectedRoster.map((player) => {
                    const status = team1AnnouncedIds.includes(player.id)
                      ? ((configForm.team1PlayingPlayers || []).includes(player.id) ? 'playing' : 'substitute')
                      : 'unannounced';
                    return (
                    <div key={`t1-squad-${player.id}`} className="small d-flex align-items-center justify-content-between gap-2">
                      <span>{player.name}</span>
                      <div className="btn-group btn-group-sm">
                        <button type="button" className={`btn btn-outline-success ${status === 'playing' ? 'active' : ''}`} onClick={() => setPlayerAnnouncementStatus('team1', player.id, 'playing')}>Playing</button>
                        <button type="button" className={`btn btn-outline-primary ${status === 'substitute' ? 'active' : ''}`} onClick={() => setPlayerAnnouncementStatus('team1', player.id, 'substitute')}>Sub</button>
                        <button type="button" className={`btn btn-outline-secondary ${status === 'unannounced' ? 'active' : ''}`} onClick={() => setPlayerAnnouncementStatus('team1', player.id, 'unannounced')}>Unannounced</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="border rounded-3 p-3 h-100">
                <h3 className="h6 mb-1">{match?.team2 || 'Team 2'} squad</h3>
                <p className="text-muted small mb-2">
                  Auto-selected squad players: {team2SelectedRoster.length}{squadSize ? ` / ${squadSize}` : ''}
                </p>
                <div className="row g-2 mb-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label small">Captain</label>
                    <select
                      className="form-select form-select-sm"
                      value={configForm.team2Captain}
                      disabled={!team2AnnouncedRoster.length}
                      onChange={handleChange('team2Captain')}
                    >
                      <option value="">Select captain</option>
                      {team2AnnouncedRoster.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label small">Vice captain</label>
                    <select
                      className="form-select form-select-sm"
                      value={configForm.team2ViceCaptain}
                      disabled={!team2AnnouncedRoster.length}
                      onChange={handleChange('team2ViceCaptain')}
                    >
                      <option value="">Select vice captain</option>
                      {team2AnnouncedRoster.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-4">
                    <label className="form-label small">Wicket keeper</label>
                    <select
                      className="form-select form-select-sm"
                      value={configForm.team2WicketKeeper}
                      disabled={!team2AnnouncedRoster.length}
                      onChange={handleChange('team2WicketKeeper')}
                    >
                      <option value="">Select wicket keeper</option>
                      {team2AnnouncedRoster.map((player) => (
                        <option key={player.id} value={player.id}>
                          {player.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  {team2SelectedRoster.map((player) => {
                    const status = team2AnnouncedIds.includes(player.id)
                      ? ((configForm.team2PlayingPlayers || []).includes(player.id) ? 'playing' : 'substitute')
                      : 'unannounced';
                    return (
                    <div key={`t2-squad-${player.id}`} className="small d-flex align-items-center justify-content-between gap-2">
                      <span>{player.name}</span>
                      <div className="btn-group btn-group-sm">
                        <button type="button" className={`btn btn-outline-success ${status === 'playing' ? 'active' : ''}`} onClick={() => setPlayerAnnouncementStatus('team2', player.id, 'playing')}>Playing</button>
                        <button type="button" className={`btn btn-outline-primary ${status === 'substitute' ? 'active' : ''}`} onClick={() => setPlayerAnnouncementStatus('team2', player.id, 'substitute')}>Sub</button>
                        <button type="button" className={`btn btn-outline-secondary ${status === 'unannounced' ? 'active' : ''}`} onClick={() => setPlayerAnnouncementStatus('team2', player.id, 'unannounced')}>Unannounced</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div>
            <button className="btn btn-primary" type="submit" disabled={isSaving || !match}>
              {isSaving ? 'Saving...' : 'Save configuration'}
            </button>
          </div>
        </div>
      </form>
      </div>
    </PageShell>
  );
};

export default MatchConfigurationPage;
